import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Chip,
  Stack
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  CameraAlt as CameraAltIcon
} from '@mui/icons-material'
import { ticketService, CreateTicketRequest } from '../../../../shared/services/TicketService'
import { categoryService } from '../../../../shared/services/CategoryService'
import { attachmentService } from '../../../../shared/services/AttachmentService'
import { OrganizationService } from '../../../../shared/services/OrganizationService'
import { CategorySummary, TicketPriority, Organization } from '../../../../shared/types/entities'
import { useAuth } from '@shared/auth/AuthContext'

interface CustomerCreateTicketProps {
  isModal?: boolean
  onSuccess?: (ticketId: string) => void
  onCancel?: () => void
}

function CustomerCreateTicket({ isModal = false, onSuccess, onCancel }: CustomerCreateTicketProps = {}) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [loadingOrganizations, setLoadingOrganizations] = useState(false)
  const [categories, setCategories] = useState<CategorySummary[]>([])
  const [oversightOrganizations, setOversightOrganizations] = useState<Organization[]>([])
  const [error, setError] = useState<string | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadingFiles, setUploadingFiles] = useState(false)
  // TODO: Add allowPrivateTickets state when organization service is implemented
  const allowPrivateTickets = false
  const [formData, setFormData] = useState<CreateTicketRequest>({
    subject: '',
    description: '',
    priority: 'Medium',
    categoryId: undefined,
    source: 'Portal',
    isPrivate: false,
    childSiteId: undefined
  })

  const isOversightUser = user?.role === 'Oversight' || (user?.oversightChildSiteIds && user.oversightChildSiteIds.length > 0)

  // Auto-detect priority from keywords in subject and description
  const detectPriorityFromText = (text: string): TicketPriority | null => {
    const lowerText = text.toLowerCase()

    // Urgent keywords
    const urgentKeywords = ['urgent', 'critical', 'emergency', 'asap', 'immediately', 'down', 'outage', 'not working', 'broken', 'production down']
    if (urgentKeywords.some(keyword => lowerText.includes(keyword))) {
      return 'Urgent'
    }

    // High priority keywords
    const highKeywords = ['important', 'high priority', 'soon', 'blocking', 'blocked', 'cannot work', "can't work"]
    if (highKeywords.some(keyword => lowerText.includes(keyword))) {
      return 'High'
    }

    // Low priority keywords
    const lowKeywords = ['minor', 'low priority', 'whenever', 'suggestion', 'feature request', 'nice to have', 'enhancement']
    if (lowKeywords.some(keyword => lowerText.includes(keyword))) {
      return 'Low'
    }

    return null
  }

  // Auto-detect and suggest priority when user types
  useEffect(() => {
    const combinedText = `${formData.subject} ${formData.description}`
    const detectedPriority = detectPriorityFromText(combinedText)

    // Only auto-update if user hasn't manually changed from default Medium
    if (detectedPriority && formData.priority === 'Medium') {
      handleChange('priority', detectedPriority)
    }
  }, [formData.subject, formData.description])

  useEffect(() => {
    loadCategories()
    if (isOversightUser && user?.userId) {
      loadOversightOrganizations()
    }
  }, [isOversightUser, user?.userId])

  const loadCategories = async () => {
    try {
      const data = await categoryService.getCategories()
      setCategories((data || []).filter(c => c.isActive))
    } catch (err: any) {
      console.error('Failed to load categories:', err)
      setCategories([])
    } finally {
      setLoadingCategories(false)
    }
  }

  const loadOversightOrganizations = async () => {
    if (!user?.userId) return

    try {
      setLoadingOrganizations(true)
      const data = await OrganizationService.getOrganizationsForOversight(user.userId)
      setOversightOrganizations(data.filter(org => org.isActive))
    } catch (err: any) {
      console.error('Failed to load oversight organizations:', err)
    } finally {
      setLoadingOrganizations(false)
    }
  }

  // TODO: Create organization service to fetch organization settings
  // For now, private tickets checkbox is hidden (allowPrivateTickets = false)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    const newFiles = Array.from(files)
    setSelectedFiles(prev => [...prev, ...newFiles])

    // Reset input so the same file can be selected again if needed
    e.target.value = ''
  }

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.subject.trim() || !formData.description.trim()) {
      setError('Subject and description are required')
      return
    }

    if (isOversightUser && oversightOrganizations.length > 0 && !formData.childSiteId) {
      setError('Please select a site for this ticket')
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Create the ticket first
      const ticket = await ticketService.createTicket(formData)

      // Upload attachments if any
      if (selectedFiles.length > 0) {
        setUploadingFiles(true)
        try {
          await Promise.all(
            selectedFiles.map(file =>
              attachmentService.uploadAttachment(file, ticket.ticketId)
            )
          )
        } catch (uploadErr: any) {
          console.error('Failed to upload some attachments:', uploadErr)
          // Continue to ticket page even if uploads fail
        } finally {
          setUploadingFiles(false)
        }
      }

      if (isModal && onSuccess) {
        onSuccess(ticket.ticketId)
      } else {
        navigate(`/customer/tickets/${ticket.ticketId}`)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create ticket')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: keyof CreateTicketRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const priorities: TicketPriority[] = ['Low', 'Medium', 'High', 'Urgent']

  const handleCancel = () => {
    if (isModal && onCancel) {
      onCancel()
    } else {
      navigate('/customer/tickets')
    }
  }

  return (
    <Box>
      {!isModal && (
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/customer/tickets')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4">Create New Ticket</Typography>
        </Box>
      )}
      {isModal && (
        <Typography variant="h5" mb={3}>Create New Ticket</Typography>
      )}

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Subject"
              required
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              disabled={loading}
              sx={{ mb: 3 }}
              helperText="Brief summary of your issue"
            />

            {isOversightUser && oversightOrganizations.length > 0 && (
              <FormControl fullWidth sx={{ mb: 3 }} required>
                <InputLabel>Site</InputLabel>
                <Select
                  value={formData.childSiteId || ''}
                  label="Site"
                  onChange={(e) => handleChange('childSiteId', e.target.value || undefined)}
                  disabled={loading || loadingOrganizations}
                >
                  <MenuItem value="">
                    <em>Select a site</em>
                  </MenuItem>
                  {oversightOrganizations.map((org) => (
                    <MenuItem key={org.organizationId} value={org.childSiteId || org.organizationId}>
                      {org.name} {org.type && `(${org.type})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              label="Description"
              required
              multiline
              rows={6}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              disabled={loading}
              sx={{ mb: 3 }}
              helperText="Detailed description of the issue you're experiencing"
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => handleChange('priority', e.target.value as TicketPriority)}
                disabled={loading}
              >
                {priorities.map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {priority}
                  </MenuItem>
                ))}
              </Select>
              {formData.priority !== 'Medium' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {detectPriorityFromText(`${formData.subject} ${formData.description}`) ?
                    '✓ Priority auto-detected based on your description' :
                    'You can change this if needed'}
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Category (Optional)</InputLabel>
              <Select
                value={formData.categoryId || ''}
                label="Category (Optional)"
                onChange={(e) => handleChange('categoryId', e.target.value || undefined)}
                disabled={loading || loadingCategories}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.categoryId} value={category.categoryId}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Tags (Optional)"
              value={formData.tags || ''}
              onChange={(e) => handleChange('tags', e.target.value)}
              disabled={loading}
              sx={{ mb: 3 }}
              helperText="Comma-separated tags (e.g., login, password, urgent)"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isGroupTicket || false}
                  onChange={(e) => handleChange('isGroupTicket', e.target.checked)}
                  disabled={loading}
                />
              }
              label="Group Ticket (all users in the same site can view and collaborate)"
              sx={{ mb: 3 }}
            />

            {/* File Attachments Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Attachments (Optional)
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                Attach files, images, or take photos to help describe your issue
              </Typography>

              <Stack direction="row" spacing={1} mb={2}>
                {/* Regular file upload button */}
                <input
                  type="file"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="file-upload-input"
                  multiple
                  accept="image/*,application/pdf,.doc,.docx,.txt,.xlsx,.xls"
                  disabled={loading}
                />
                <label htmlFor="file-upload-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AttachFileIcon />}
                    disabled={loading}
                  >
                    Choose Files
                  </Button>
                </label>

                {/* Camera capture button for mobile */}
                <input
                  type="file"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="camera-capture-input"
                  accept="image/*"
                  capture="environment"
                  disabled={loading}
                />
                <label htmlFor="camera-capture-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CameraAltIcon />}
                    disabled={loading}
                  >
                    Take Photo
                  </Button>
                </label>
              </Stack>

              {/* Selected files preview */}
              {selectedFiles.length > 0 && (
                <Stack spacing={1}>
                  {selectedFiles.map((file, index) => (
                    <Chip
                      key={`${file.name}-${index}`}
                      label={`${file.name} (${formatFileSize(file.size)})`}
                      onDelete={() => handleRemoveFile(index)}
                      deleteIcon={<CloseIcon />}
                      variant="outlined"
                      sx={{ justifyContent: 'space-between', maxWidth: '100%' }}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {allowPrivateTickets && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isPrivate || false}
                    onChange={(e) => handleChange('isPrivate', e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Make this ticket private (only you can see it)"
                sx={{ mb: 3 }}
              />
            )}

            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading || uploadingFiles}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={loading || uploadingFiles ? <CircularProgress size={20} /> : <SendIcon />}
                disabled={loading || uploadingFiles}
              >
                {uploadingFiles ? 'Uploading files...' : loading ? 'Creating...' : 'Create Ticket'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default CustomerCreateTicket